import { useState } from "react";
import { cn } from "@/lib/utils";
import { ClickableRow } from "@/components/shared/ClickableRow";
import type { MyTask } from "@/hooks/use-my-tasks";
import { SrIcon } from "@/components/ui/SrIcon";
import type { ProjectCommentUnread } from "@/hooks/use-project-comment-reads";
import { MyTasksRow } from "./MyTasksRow";
import { ProjectGroupUpdatesIndicator } from "./ProjectGroupUpdatesIndicator";

interface MyTasksProjectGroupProps {
  projectId: string;
  projectName: string;
  tasks: MyTask[];
  userId: string | null;
  colSpan: number;
  onNavigate?: (projectId: string) => void;
  defaultExpanded?: boolean;
  /** VIB-2180: ulest-oppsummering for prosjektets oppdateringer. */
  unread?: ProjectCommentUnread;
  /** VIB-2180: åpner ProjectUpdatesDialog. Udefinert = flagg av, ingen ikon. */
  onOpenUpdates?: (projectId: string) => void;
}

/**
 * Collapsible per-project header row used by MyTasksList to group tasks
 * by project. Shows project name, ProjectProgressBar and task counts.
 */
export function MyTasksProjectGroup({
  projectId,
  projectName,
  tasks,
  userId,
  colSpan,
  onNavigate,
  defaultExpanded = false,
  unread,
  onOpenUpdates,
}: MyTasksProjectGroupProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const activeCount = tasks.filter((t) => t.task_status === "in_progress").length;

  return (
    <>
      <ClickableRow
        expanded={expanded}
        onActivate={() => setExpanded(!expanded)}
        className={cn(
          "border-b border-border/50 transition-colors",
          expanded ? "bg-primary/5" : "hover:bg-muted/20",
        )}
      >
        <td colSpan={colSpan} className="px-4 py-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate text-sm">{projectName}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-[11px] text-muted-foreground tabular-nums">
                {tasks.length} oppgaver{activeCount > 0 ? ` · ${activeCount} pågår` : ""}
              </span>
              {onOpenUpdates && (
                <ProjectGroupUpdatesIndicator
                  unread={unread}
                  onOpen={() => onOpenUpdates(projectId)}
                />
              )}
              {/* VIB-1200: gruppe-headeren gir en direkte snarvei til prosjektet
                  uten å ekspandere/kollapse gruppen (stopPropagation). */}
              <button
                type="button"
                aria-label="Gå til prosjekt"
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate?.(projectId);
                }}
                className="inline-flex items-center justify-center w-10 h-10 -my-3 text-muted-foreground hover:text-foreground shrink-0"
              >
                <SrIcon name="external-link" className="w-4 h-4" />
              </button>
              {expanded ? (
                <SrIcon name="chevron-up" className="w-4 h-4 text-muted-foreground" />
              ) : (
                <SrIcon name="chevron-down" className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
          </div>
        </td>
      </ClickableRow>
      {expanded &&
        tasks.map((t) => (
          <MyTasksRow
            key={t.id}
            task={t}
            showProject={false}
            showGoToProject
            userId={userId}
            onNavigate={onNavigate}
          />
        ))}
    </>
  );
}

export default MyTasksProjectGroup;
